/**
 * Voice anatomical site → EPMS sitePos (0 = left cell).
 *
 * Pearl display puts distal on the left for teeth 1–8 and 25–32.
 * Teeth 9–24 stay mesial-left. Walk order is a different list — do not
 * reuse the lingual walk-back here.
 *
 * This is the only EPMS mesial/distal remap. `PerioGridSource` already
 * hands named sites (MB = mesial). Do not invert again to "fix" a blob.
 */

import type { ScribeSiteCode } from '../../perio/scribe-sites';
import { facialSitesLtr, lingualSitesLtr } from '../../perio/odontogram';

const FACIAL_SITES: ReadonlySet<string> = new Set(['MB', 'B', 'DB']);
const LINGUAL_SITES: ReadonlySet<string> = new Set(['ML', 'L', 'DL']);

/**
 * Column index for a Voice site on one EPMS tooth.
 *
 * @param tooth - Universal tooth number 1–32
 * @param site - Pearl anatomical site
 * @returns 0 (left), 1 (mid), or 2 (right); undefined when the site is unknown
 * @example
 * epmsSiteIndex(3, 'DB') // 0
 * epmsSiteIndex(9, 'MB') // 0
 * epmsSiteIndex(3, 'MB') // 2
 */
export function epmsSiteIndex(
  tooth: number,
  site: string,
): 0 | 1 | 2 | undefined {
  let trio: readonly ScribeSiteCode[] | null = null;
  if (FACIAL_SITES.has(site)) trio = facialSitesLtr(tooth);
  else if (LINGUAL_SITES.has(site)) trio = lingualSitesLtr(tooth);
  if (!trio) return undefined;
  const index = trio.indexOf(site as ScribeSiteCode);
  if (index !== 0 && index !== 1 && index !== 2) return undefined;
  return index;
}
